import { ClosedLetterIcon, OpenInNewIcon, OpenedLetterIcon, TapIcon } from '@systemeio/ui/icons'
import { DateRangePicker, Tooltip, getDate, getLast3MonthsCustomFilter } from '@systemeio/ui'
import { useContactEmails } from 'modules/contacts/contact-page/hooks/use-contact-emails'
import {
  ContactEmailsFilterType,
  ContactEmailsInterface,
} from 'modules/contacts/contact-page/types/contact-emails-interface'
import { ContactId, ContactInterface } from 'modules/contacts/types/contact-interface'
import React, { useEffect } from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { OrderEnum } from 'shared/types/pagination-interface'
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format'
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates'

interface ContactEmailsProps {
  contactId: ContactId
  contact?: ContactInterface
}

const defaultFilters: Required<Pick<ContactEmailsFilterType, 'date'>> = { date: [null, null] }

const ContactEmails = ({ contactId, contact }: ContactEmailsProps) => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const { setPagination, pagination, setLimit, limit, filter, setFilter } = useFilterPagination(
    defaultFilters,
    true,
  )
  const { contactEmails } = useContactEmails({
    contactId,
    limit,
    pagination,
    filter,
    shouldFetch: filter.date[0] !== null,
  })

  useEffect(() => {
    if (contact) {
      setFilter(prev => ({
        ...prev,
        date: getDateRangeSinceDate(contact.createdAt, user?.timezone),
      }))
    }
  }, [contact, setFilter, user?.timezone])

  const columns: ColumnDefinitionInterface<ContactEmailsInterface>[] = [
    {
      key: 'email',
      header: t('dashboard.contact_page.subject'),
      widthPercent: 70,
      onRender: email => (
        <div className={'flex w-full gap-1.5'}>
          {email.openedAt !== null ? (
            <Tooltip
              mode={'hover'}
              label={
                <div className={'flex gap-1'}>
                  <span className={'text-text-primary'}>
                    {t('dashboard.contact_page.email_opened_at')}
                  </span>
                  <span className={'text-text-primary'}>
                    {dateFormat(email.openedAt, {
                      dateFormat: user?.dateFormat,
                      timeZone: user?.timezone,
                    })}
                  </span>
                </div>
              }
            >
              <OpenedLetterIcon className={'h-[20px] w-[20px] shrink-0'} />
            </Tooltip>
          ) : (
            <ClosedLetterIcon className={'h-[20px] w-[20px] shrink-0'} />
          )}
          <Tooltip
            wrapperClassName={'w-full truncate'}
            mode={'overflow'}
            label={email.email.subject}
          >
            {email.email.subject}
          </Tooltip>
          {email.clicked && (
            <Tooltip
              wrapperClassName={'flex w-fit justify-end'}
              mode={'hover'}
              label={t('dashboard.contact_page.email_clicked_popover')}
            >
              <TapIcon className={'fill-brand-primary h-[20px] w-[20px] shrink-0'} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('dashboard.contact_page.sent_at'),
      widthPercent: 30,
      onRender: email => (
        <Tooltip
          wrapperClassName={'w-full'}
          mode={'hover'}
          label={dateTimeFormat(email.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        >
          <span className={'w-full truncate'}>
            {dateFormat(email.createdAt, {
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </Tooltip>
      ),
      onRenderSmall: email => (
        <div className={'flex items-center gap-3'}>
          <span className={'text-text-primary font-bold'}>
            {t('dashboard.contact_page.sent_at')}
          </span>
          <span>
            {dateTimeFormat(email.createdAt, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </div>
      ),
    },
  ]
  return (
    <ListingLayout
      counter={false}
      data={contactEmails}
      type={'default'}
      title={
        contact ? (
          <LinkWithoutPrefetch
            className={
              'text-text-primary hover:text-brand-primary focus-visible:text-brand-primary group text-[17px] outline-hidden transition-colors duration-200 ease-linear'
            }
            href={`/contacts/${contact.id}/email-history`}
          >
            <div className={'flex items-center gap-1.5'}>
              {t('dashboard.contact_page.email_history')}
              <OpenInNewIcon
                className={
                  'fill-text-primary group-hover:fill-brand-primary transition-colors duration-200 ease-linear'
                }
              />
            </div>
          </LinkWithoutPrefetch>
        ) : (
          <span className={'text-[17px]'}>{t('dashboard.contact_page.email_history')}</span>
        )
      }
      customHeaderElement={
        <DateRangePicker
          maxDate={getToday(user?.timezone)}
          minDate={getDate(contact?.createdAt, user?.timezone)}
          getDefaultDate={() => getDateRangeSinceDate(contact?.createdAt, user?.timezone)}
          date={filter.date}
          customFilters={[getLast3MonthsCustomFilter(user?.timezone)]}
          onChange={date => {
            setPagination({ startFromId: undefined, order: OrderEnum.next })
            setLimit(10)
            setFilter(prev => ({ ...prev, date: date }))
          }}
          timezone={user?.timezone}
          createdAt={user?.createdAt}
          intlLocale={intlLocale}
        />
      }
    >
      <Table
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        data={contactEmails}
        columns={columns}
      />
    </ListingLayout>
  )
}

export default ContactEmails
